import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Copy, Download, RefreshCw, ScrollText, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';

type BridgeLogInfo = { bridgeId: string; clientId: string | null; clientName: string | null; mac: string | null; size: number; updatedAt: string | null };

const selectCls = 'flex h-9 w-full rounded-md border border-input bg-input-background px-3 text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50';

const lineClass = (l: string) => {
  if (/\[ ERROR \]/.test(l)) return 'text-red-600 dark:text-red-400';
  if (/\[WARNING\]/.test(l)) return 'text-amber-600 dark:text-amber-400';
  if (/\[SUCCESS\]/.test(l)) return 'text-emerald-600 dark:text-emerald-400';
  if (/ on COM\d+ .*last minute/i.test(l)) return 'font-semibold text-sky-600 dark:text-sky-400';
  return 'text-foreground/80';
};

const ago = (d: string | null) => {
  if (!d) return 'never';
  const s = Math.max(0, Math.round((Date.now() - new Date(d).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return new Date(d).toLocaleString();
};

/** Live view of the log each bridge PC sends to the portal. */
export function BridgeLogsTab({ token }: { token: string }) {
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [bridges, setBridges] = useState<BridgeLogInfo[]>([]);
  const [bridgeId, setBridgeId] = useState('');
  const [lines, setLines] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [follow, setFollow] = useState(true);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const loadBridges = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/bridge-logs', { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not load bridges');
      setBridges(data.bridges || []);
      setBridgeId((prev) => prev || data.bridges?.[0]?.bridgeId || '');
    } catch (e: any) {
      toast.error(e.message);
    }
  }, [headers]);

  const loadLines = useCallback(async () => {
    if (!bridgeId) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/bridge-logs/${encodeURIComponent(bridgeId)}?lines=2000`, { headers });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not load the log');
      setLines(data.lines || []);
    } catch (e: any) {
      setLines([]);
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [bridgeId, headers]);

  useEffect(() => { loadBridges(); }, [loadBridges]);
  useEffect(() => { loadLines(); }, [loadLines]);
  useEffect(() => {
    if (!follow) return;
    const id = setInterval(() => { loadLines(); loadBridges(); }, 5000);
    return () => clearInterval(id);
  }, [follow, loadLines, loadBridges]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? lines.filter((l) => l.toLowerCase().includes(q)) : lines;
  }, [lines, search]);

  useEffect(() => {
    if (follow && boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [shown, follow]);

  const current = bridges.find((b) => b.bridgeId === bridgeId);
  const text = shown.join('\n');
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); toast.success(`Copied ${shown.length} lines`); } catch { toast.error('Copy failed'); }
  };
  const download = () => {
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `bridge-log-${bridgeId}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="flex items-center gap-2 text-2xl font-bold"><ScrollText className="h-6 w-6" /> Bridge Logs</h2>
        <p className="text-sm text-muted-foreground">The log of each bridge PC, sent to the portal every few seconds (bridge version from 31 Oct 2026 onward).</p>
      </div>

      <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
        <select className={`${selectCls} lg:w-[420px]`} value={bridgeId} onChange={(e) => setBridgeId(e.target.value)} aria-label="Bridge">
          {bridges.length === 0 && <option value="">No bridge has sent a log yet</option>}
          {bridges.map((b) => (
            <option key={b.bridgeId} value={b.bridgeId}>
              {(b.clientName || 'Unknown client')}{b.mac ? ` · ${b.mac}` : ''} · {ago(b.updatedAt)}
            </option>
          ))}
        </select>
        <div className="relative lg:w-[260px]">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input className="pl-8" placeholder="Filter, e.g. COM3 or VFD &quot;3&quot;" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <Switch checked={follow} onCheckedChange={setFollow} aria-label="Follow live" /> Live
        </label>
        <div className="flex gap-2 lg:ml-auto">
          <Button variant="outline" size="sm" onClick={() => { loadBridges(); loadLines(); }} disabled={loading}>
            <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={copy} disabled={!shown.length}><Copy className="mr-1.5 h-4 w-4" /> Copy</Button>
          <Button variant="outline" size="sm" onClick={download} disabled={!shown.length}><Download className="mr-1.5 h-4 w-4" /> Download</Button>
        </div>
      </div>

      {current && (
        <p className="text-xs text-muted-foreground">
          Bridge <span className="font-mono">{current.bridgeId}</span> · last line {ago(current.updatedAt)} · {shown.length} of {lines.length} lines shown
        </p>
      )}

      <div ref={boxRef} className="h-[65vh] overflow-auto rounded-lg border bg-muted/30 p-3 font-mono text-xs leading-relaxed">
        {shown.length === 0
          ? <p className="py-10 text-center text-muted-foreground">{bridgeId ? 'No lines' : 'Install the latest bridge kit; its log appears here within a few seconds.'}</p>
          : shown.map((l, i) => <div key={i} className={`whitespace-pre-wrap break-all ${lineClass(l)}`}>{l}</div>)}
      </div>
    </div>
  );
}
